import * as Sentry from '@sentry/react';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { isBeforeDate } from '@shared/helpers/date.helpers';
import { latestAiProjectUpdatedAt } from '@shared/helpers/ai-project-sync.helpers';
import type { AiToolCall } from '@shared/models/ai-chat.model';
import type { Project } from '@shared/models/project.model';
import { UpdateClientAction } from '@client/store/store.model';
import {
  fetchProject,
  isProjectUpdateQueueBusy,
  useProjectStateStore,
  waitForProjectUpdateQueueIdle,
} from '@client/store/project/project.store';

/** Open-project fields the adopt decision reads. */
export type AiProjectSyncSnapshot = {
  id: number;
  updated_at: string;
  enriched?: boolean;
};

export type AiProjectAdoptDecision =
  | { kind: 'skip' }
  | { kind: 'defer'; projectId: number }
  | { kind: 'adopt'; projectId: number };

type ConversationForSync = {
  projectId: number | null;
  messages: readonly { toolCalls?: readonly AiToolCall[] | undefined }[];
};

export type AiProjectSyncDeps = {
  getOpenProject: () => AiProjectSyncSnapshot | undefined;
  isWritePending: () => boolean;
  /** Resolves when the project PUT queue is idle; used to retry a deferred adopt. */
  waitForWriteIdle: () => Promise<void>;
  fetchProject: (id: number) => Promise<Project>;
  commitProject: (project: Project) => void;
  /** Reports failures from fire-and-forget idle retries (post-fetch defer). */
  onError: (error: unknown) => void;
};

const inFlightByProjectId = new Map<number, Promise<void>>();

type IdleRetry = {
  conversation: ConversationForSync;
  promise: Promise<void>;
};

const idleRetryByProjectId = new Map<number, IdleRetry>();

const defaultAiProjectSyncDeps = (): AiProjectSyncDeps => ({
  getOpenProject: () => useProjectStateStore.getState().project,
  isWritePending: isProjectUpdateQueueBusy,
  waitForWriteIdle: waitForProjectUpdateQueueIdle,
  fetchProject,
  commitProject: (project) => {
    useProjectStateStore.getState().updateProjectLocally({
      action: UpdateClientAction.Update,
      itemOrId: project,
    });
  },
  onError: (error) => {
    Sentry.captureException(error);
  },
});

/**
 * Whether the open project should be replaced by the server row an AI turn
 * already saved. Skips optimistic local edits. Returns {@link AiProjectAdoptDecision}
 * `defer` when a project PUT is in flight so the caller can retry after idle.
 */
export const decideAiProjectAdopt = (input: {
  conversationProjectId: number | null;
  openProject: AiProjectSyncSnapshot | undefined;
  messages: ConversationForSync['messages'];
  writePending: boolean;
}): AiProjectAdoptDecision => {
  const open = input.openProject;
  if (!open || open.id === 0) return { kind: 'skip' };
  // Optimistic edits set enriched false until they persist.
  if (open.enriched === false) return { kind: 'skip' };
  if (input.conversationProjectId !== open.id) return { kind: 'skip' };

  const stamp = latestAiProjectUpdatedAt(input.messages);
  if (!stamp) return { kind: 'skip' };
  if (!isBeforeDate(open.updated_at, stamp)) return { kind: 'skip' };

  if (input.writePending) return { kind: 'defer', projectId: open.id };
  return { kind: 'adopt', projectId: open.id };
};

/**
 * Wait for the PUT queue to drain, then re-run sync with the latest conversation
 * seen for this project. Coalesces concurrent defers onto one idle wait.
 */
const scheduleAdoptAfterIdle = (
  projectId: number,
  conversation: ConversationForSync,
  deps: AiProjectSyncDeps,
): Promise<void> => {
  const existing = idleRetryByProjectId.get(projectId);
  if (existing) {
    existing.conversation = conversation;
    return existing.promise;
  }

  const entry: IdleRetry = {
    conversation,
    promise: Promise.resolve(),
  };

  entry.promise = (async () => {
    try {
      await deps.waitForWriteIdle();
    } finally {
      // Release even when wait fails so a later conversation can schedule again.
      if (idleRetryByProjectId.get(projectId) === entry) {
        idleRetryByProjectId.delete(projectId);
      }
    }
    await syncOpenProjectFromAiConversation(entry.conversation, deps);
  })();

  idleRetryByProjectId.set(projectId, entry);
  return entry.promise;
};

const adoptFetchedProject = async (
  projectId: number,
  conversation: ConversationForSync,
  deps: AiProjectSyncDeps,
): Promise<void> => {
  const fresh = await deps.fetchProject(projectId);
  const still = decideAiProjectAdopt({
    conversationProjectId: conversation.projectId,
    openProject: deps.getOpenProject(),
    messages: conversation.messages,
    writePending: deps.isWritePending(),
  });

  switch (still.kind) {
    case 'skip':
      return;
    case 'defer':
      // Do not await: this task still owns inFlightByProjectId until return.
      void scheduleAdoptAfterIdle(projectId, conversation, deps).catch(
        deps.onError,
      );
      return;
    case 'adopt':
      break;
    default:
      assertUnreachable(still);
  }

  if (fresh.id !== projectId) return;

  const current = deps.getOpenProject();
  if (!current) return;
  // The socket may have landed the same row while this GET was in flight.
  if (!isBeforeDate(current.updated_at, fresh.updated_at)) return;

  deps.commitProject(fresh);
};

const startAdopt = (
  projectId: number,
  conversation: ConversationForSync,
  deps: AiProjectSyncDeps,
): Promise<void> => {
  const existing = inFlightByProjectId.get(projectId);
  if (existing) return existing;

  const task = adoptFetchedProject(projectId, conversation, deps).finally(
    () => {
      inFlightByProjectId.delete(projectId);
    },
  );
  inFlightByProjectId.set(projectId, task);
  return task;
};

/**
 * Refetch the open project when a conversation carries a newer AI save than
 * the store. One fetch per project at a time. Re-reads the store after the
 * await so a local edit started during the GET is left in place. Defers until
 * the project PUT queue is idle when a write is in flight.
 */
export const syncOpenProjectFromAiConversation = (
  conversation: ConversationForSync,
  deps: AiProjectSyncDeps = defaultAiProjectSyncDeps(),
): Promise<void> => {
  const decision = decideAiProjectAdopt({
    conversationProjectId: conversation.projectId,
    openProject: deps.getOpenProject(),
    messages: conversation.messages,
    writePending: deps.isWritePending(),
  });

  switch (decision.kind) {
    case 'skip':
      return Promise.resolve();
    case 'defer':
      return scheduleAdoptAfterIdle(decision.projectId, conversation, deps);
    case 'adopt':
      return startAdopt(decision.projectId, conversation, deps);
    default:
      return assertUnreachable(decision);
  }
};
